import React, { useState } from 'react';
import { eventAPI } from '../../lib/eventApi';
import { toast } from 'sonner';
import SharedPartnerPicker from './SharedPartnerPicker';
import ConfirmDialog from './ConfirmDialog';
import { UsersThree, Trash, Pencil, X } from '@phosphor-icons/react';

// SharedPartnerRow — admin/EM-side compact control for managing a
// registration's shared partner. Renders directly inside the player row
// in RegistrationSetupTab. Works PRE and POST session start (never
// touches courts or scoring).
//
// Props:
//   sessionId        — current session id
//   registrationId   — the player's session_registrations.id
//   sharedPartner    — current shared partner object (or null/undefined)
//                      shape: { shared_profile_id, shared_player_name, … }
//   onChange         — () => void, called after successful save/remove
export default function SharedPartnerRow({ sessionId, registrationId, sharedPartner, onChange }) {
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState(sharedPartner?.shared_profile_id || null);
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const save = async () => {
    if (!selected) {
      toast.error('Pick a member first');
      return;
    }
    setBusy(true);
    try {
      await eventAPI.setSharedPartner(sessionId, registrationId, { shared_profile_id: selected });
      toast.success('Shared partner saved');
      setEditing(false);
      await onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await eventAPI.removeSharedPartner(sessionId, registrationId);
      toast.success('Shared partner removed');
      setSelected(null);
      setEditing(false);
      setConfirmRemove(false);
      await onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not remove');
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <div
        className="mt-1.5 p-2 rounded-md bg-white border border-[#e3dcd1] space-y-2"
        data-testid={`shared-edit-${registrationId}`}
      >
        <SharedPartnerPicker
          sessionId={sessionId}
          value={selected}
          onChange={setSelected}
          testidPrefix={`shared-edit-picker-${registrationId}`}
        />
        <div className="flex items-center gap-1.5">
          <button
            onClick={save}
            disabled={busy || !selected}
            className="text-[11px] px-2 py-1 rounded-md bg-[#015e3f] text-white disabled:opacity-50"
            data-testid={`shared-save-${registrationId}`}
          >
            Save
          </button>
          <button
            onClick={() => { setEditing(false); setSelected(sharedPartner?.shared_profile_id || null); }}
            disabled={busy}
            className="text-[11px] px-2 py-1 rounded-md border border-[#e3dcd1] text-[#6a766e]"
            data-testid={`shared-cancel-${registrationId}`}
          >
            <X size={11} /> Cancel
          </button>
          {sharedPartner && (
            <button
              onClick={() => setConfirmRemove(true)}
              disabled={busy}
              className="ml-auto text-[11px] px-2 py-1 rounded-md border border-red-300 text-red-600 hover:bg-red-50"
              data-testid={`shared-remove-${registrationId}`}
            >
              <Trash size={11} /> Remove
            </button>
          )}
        </div>
        <ConfirmDialog
          open={confirmRemove}
          onOpenChange={setConfirmRemove}
          title="Remove shared partner?"
          description="The pair breaks — the primary registration stays but the shared partner is unlinked."
          confirmLabel="Remove"
          destructive
          busy={busy}
          onConfirm={remove}
        />
      </div>
    );
  }

  if (sharedPartner) {
    return (
      <div
        className="mt-1 flex items-center gap-1.5 text-[11px] text-[#6a766e]"
        data-testid={`shared-display-${registrationId}`}
      >
        <UsersThree size={11} className="text-[#015e3f]" />
        <span>
          Shared with <span className="text-[#013523]" data-testid={`shared-name-${registrationId}`}>{sharedPartner.shared_player_name}</span>
        </span>
        <button
          onClick={() => setEditing(true)}
          className="ml-1 underline text-[#015e3f]"
          data-testid={`shared-edit-btn-${registrationId}`}
        >
          <Pencil size={10} className="inline" /> edit
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => setEditing(true)}
      className="mt-1 text-[11px] text-[#015e3f] underline"
      data-testid={`shared-add-btn-${registrationId}`}
    >
      + Add shared partner
    </button>
  );
}
